import type { JSX } from 'react';

export interface MissingLocalEnvPageProps {
  /** Defaults to `import.meta.env.PROD` — injectable for tests. */
  readonly isProd?: boolean;
}

/** Shown when Cognito / site Vite env is missing — avoids a blank page. */
export function MissingLocalEnvPage({
  isProd = import.meta.env.PROD,
}: MissingLocalEnvPageProps = {}): JSX.Element {
  if (isProd) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-xl space-y-4 rounded border border-amber-200 bg-amber-50 p-6 text-sm text-amber-950">
          <h1 className="text-lg font-semibold">FX Portal is misconfigured</h1>
          <p>
            This deploy build is missing Cognito OIDC or site Vite environment variables (
            <code className="rounded bg-white px-1">VITE_COGNITO_FX_CLIENT_ID</code>,{' '}
            <code className="rounded bg-white px-1">VITE_COGNITO_FX_USER_POOL_ID</code>,{' '}
            <code className="rounded bg-white px-1">VITE_FX_PORTAL_BASE_URL</code>
            ). Sign-in cannot start.
          </p>
          <p>
            CI must bake these from SSM under{' '}
            <code className="rounded bg-white px-1">/nrx-platform/&lt;stage&gt;/</code> during the
            static-app build (see{' '}
            <code className="rounded bg-white px-1">release-environment.yml</code>). Redeploy after
            fixing the bake-in step.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <div className="max-w-xl space-y-4 rounded border border-amber-200 bg-amber-50 p-6 text-sm text-amber-950">
        <h1 className="text-lg font-semibold">FX Portal needs local environment variables</h1>
        <p>
          The dev server is running, but Cognito OIDC is not configured. Without{' '}
          <code className="rounded bg-white px-1">VITE_COGNITO_FX_CLIENT_ID</code>,{' '}
          <code className="rounded bg-white px-1">VITE_COGNITO_FX_USER_POOL_ID</code>, and{' '}
          <code className="rounded bg-white px-1">VITE_FX_PORTAL_BASE_URL</code>, sign-in cannot
          start and the app stays blank.
        </p>
        <p>
          Copy <code className="rounded bg-white px-1">.env.example</code> to{' '}
          <code className="rounded bg-white px-1">.env.local</code>, fill values from SSM under{' '}
          <code className="rounded bg-white px-1">/nrx-platform/sandbox/l3-apps/</code> (
          <code className="rounded bg-white px-1">cognito-fx-app-client-id</code>,{' '}
          <code className="rounded bg-white px-1">fx-portal-site-url</code>) and{' '}
          <code className="rounded bg-white px-1">l2-shared/cognito-fx-pool-id</code>, set{' '}
          <code className="rounded bg-white px-1">
            VITE_FX_PORTAL_BASE_URL=http://localhost:5180
          </code>
          , then restart the dev server.
        </p>
      </div>
    </main>
  );
}
